// Global Zones Table Component - Revamped to match Customers table design

import React, { useState, useMemo } from 'react'
import {
  Box,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Tooltip,
  Typography,
  IconButton,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import intl from 'localization/components'
import { useIntl } from 'react-intl'
import { countries as Countries } from 'utils/constants'

interface ZoneData {
  name: string
  countries: string[]
}

interface GlobalZonesTableProps {
  data: ZoneData[]
  onCreateZone: () => void
  onEditZone: (zone: ZoneData) => void
}

const GlobalZonesTable: React.FC<GlobalZonesTableProps> = ({
  data,
  onCreateZone,
  onEditZone,
}) => {
  const { messages } = useIntl()
  const [currentPage, setCurrentPage] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const pageSize = 25

  // Filter data based on search, always rendering zones in name order
  // (A, B, C, …, then custom zones alphabetically).
  const filteredData = useMemo(() => {
    const sorted = [...data].sort((a, b) => a.name.localeCompare(b.name))
    if (!searchQuery.trim()) return sorted

    const query = searchQuery.toLowerCase()
    return sorted.filter((zone) => {
      const nameMatch = zone.name.toLowerCase().includes(query)
      const countryMatch = zone.countries.some((code) => {
        const countryName = Countries.getName(code, 'en', {
          select: 'official',
        })
        return (
          countryName?.toLowerCase().includes(query) ||
          code.toLowerCase().includes(query)
        )
      })
      return nameMatch || countryMatch
    })
  }, [data, searchQuery])

  // Calculate pagination
  const totalPages = Math.ceil(filteredData.length / pageSize)
  const paginatedData = useMemo(() => {
    const start = currentPage * pageSize
    return filteredData.slice(start, start + pageSize)
  }, [filteredData, currentPage, pageSize])

  const startRow = currentPage * pageSize + 1
  const endRow = Math.min((currentPage + 1) * pageSize, filteredData.length)

  // Reset to first page when search changes
  React.useEffect(() => {
    setCurrentPage(0)
  }, [searchQuery])

  const getPageNumbers = () => {
    const pages: (number | string)[] = []
    const maxVisiblePages = 5

    if (totalPages <= maxVisiblePages) {
      for (let i = 0; i < totalPages; i++) {
        pages.push(i)
      }
    } else {
      pages.push(0)
      if (currentPage > 2) pages.push('...')
      const start = Math.max(1, currentPage - 1)
      const end = Math.min(totalPages - 2, currentPage + 1)
      for (let i = start; i <= end; i++) {
        if (!pages.includes(i)) pages.push(i)
      }
      if (currentPage < totalPages - 3) pages.push('...')
      if (!pages.includes(totalPages - 1)) pages.push(totalPages - 1)
    }
    return pages
  }

  // Render country chips
  const renderCountries = (countries: string[]) => {
    if (!countries || countries.length === 0) {
      return (
        <Typography sx={{ color: 'text.disabled', fontSize: 14 }}>
          None
        </Typography>
      )
    }

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        {countries.map((countryCode) => {
          const countryName = Countries.getName(countryCode, 'en', {
            select: 'official',
          })
          if (!countryName) return null

          return (
            <Chip
              key={countryCode}
              size="small"
              label={countryName}
              sx={{
                fontSize: 12,
                height: 24,
                bgcolor: 'action.selected',
                color: 'text.primary',
                '& .MuiChip-label': {
                  px: 1,
                },
              }}
            />
          )
        })}
      </Box>
    )
  }

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      {/* Card Header */}
      <Box
        sx={{
          px: 3,
          py: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 600,
              color: 'text.primary',
            }}
          >
            All Zones
          </Typography>
          <Typography
            sx={{
              fontSize: 13,
              color: 'text.secondary',
              mt: 0.25,
            }}
          >
            Manage global zone configurations
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Search Bar */}
          <TextField
            size="small"
            placeholder="Search by zone name or country..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              width: 300,
              '& .MuiOutlinedInput-root': {
                bgcolor: 'action.hover',
                borderRadius: 1,
                fontSize: 13,
                '& fieldset': {
                  borderColor: 'divider',
                },
                '&:hover fieldset': {
                  borderColor: 'primary.300',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                },
              },
              '& .MuiOutlinedInput-input': {
                py: 1,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onCreateZone}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
          >
            {messages[intl.customerZones('custom-zone-create-cta')] as string}
          </Button>
        </Box>
      </Box>

      {/* Table */}
      <TableContainer sx={{ maxHeight: 'calc(100vh - 340px)' }}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  width: 200,
                  py: 1.5,
                  px: 2.5,
                  bgcolor: 'action.hover',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    color: 'text.secondary',
                  }}
                >
                  Name
                </Typography>
              </TableCell>
              <TableCell
                sx={{
                  py: 1.5,
                  px: 2.5,
                  bgcolor: 'action.hover',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    color: 'text.secondary',
                  }}
                >
                  Countries
                </Typography>
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  width: 80,
                  py: 1.5,
                  px: 2.5,
                  bgcolor: 'action.hover',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              />
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} sx={{ py: 8, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    {searchQuery
                      ? 'No zones match your search'
                      : 'No zones found'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedData.map((zone) => (
                <TableRow
                  key={zone.name}
                  sx={{
                    bgcolor: 'background.paper',
                    '&:hover': {
                      bgcolor: (theme) =>
                        theme.palette.mode === 'dark'
                          ? 'rgba(84, 189, 203, 0.08)'
                          : 'primary.50',
                    },
                  }}
                >
                  <TableCell
                    sx={{
                      width: 200,
                      py: 1.75,
                      px: 2.5,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: 14,
                        color: 'text.primary',
                      }}
                    >
                      {zone.name}
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      py: 1.75,
                      px: 2.5,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    {renderCountries(zone.countries)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      width: 80,
                      py: 1.75,
                      px: 2.5,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Tooltip
                      title={
                        messages[
                          intl.customerZones('zone-edit-tooltip')
                        ] as string
                      }
                    >
                      <IconButton
                        size="small"
                        aria-label={`edit-zone-${zone.name}`}
                        onClick={() => onEditZone(zone)}
                        sx={{ color: 'text.secondary' }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {filteredData.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Showing {startRow} – {endRow} of {filteredData.length} zones
          </Typography>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 0}
                sx={{
                  width: 32,
                  height: 32,
                  color: 'text.secondary',
                  '&:hover': { bgcolor: 'action.hover' },
                  '&.Mui-disabled': { color: 'action.disabled' },
                }}
              >
                <ChevronLeftIcon fontSize="small" />
              </IconButton>

              {getPageNumbers().map((page, index) =>
                page === '...' ? (
                  <Typography
                    key={`ellipsis-${index}`}
                    sx={{ px: 1, color: 'text.disabled' }}
                  >
                    ...
                  </Typography>
                ) : (
                  <IconButton
                    key={page}
                    size="small"
                    onClick={() => setCurrentPage(page as number)}
                    sx={{
                      width: 32,
                      height: 32,
                      fontSize: 13,
                      fontWeight: 500,
                      borderRadius: 1,
                      ...(currentPage === page
                        ? {
                            bgcolor: 'primary.main',
                            color: 'primary.contrastText',
                            '&:hover': { bgcolor: 'primary.dark' },
                          }
                        : {
                            color: 'text.secondary',
                            '&:hover': { bgcolor: 'action.hover' },
                          }),
                    }}
                  >
                    {(page as number) + 1}
                  </IconButton>
                ),
              )}

              <IconButton
                size="small"
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage >= totalPages - 1}
                sx={{
                  width: 32,
                  height: 32,
                  color: 'text.secondary',
                  '&:hover': { bgcolor: 'action.hover' },
                  '&.Mui-disabled': { color: 'action.disabled' },
                }}
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>
      )}
    </Paper>
  )
}

export default GlobalZonesTable
